import React, {
  createContext,
  startTransition,
  useEffect,
  useMemo,
  useRef,
} from "react";
// import { merge, tap } from "rxjs";
import AppModel, { app, AppAttributes } from "../model/AppModel";
import CartModel from "../model/CartModel";
// TBT-FIX: `SetupModel` + `SetupPrivateModel` sortent du graphe eager de
// `app.js` (instantiation déjà différée via `runAfterLcp` plus bas).
// On garde uniquement les types pour l'API du contexte.
import type SetupModelType from "../model/SetupModel";
import type SetupPrivateModelType from "../model/SetupPrivateModel";
import { useDispatch } from "react-redux";
import CustomerModel from "@src/backbone/model/CustomerModel";
import { AppSlice } from "@src/store/reducers/AppSlice";
import { cartSlice } from "@src/store/reducers/CartSlice";
import { isValidCartStoreId } from "@src/store/reducers/CartSlice/selectors";
import { useAlertNoticeContext } from "@src/lib/alert-notice/useAlertNoticeContext";
import { useRollingStartNavigation } from "@src/lib/rolling-start-navigation";
import { IRollingStartNavigation } from "@src/lib/rolling-start-navigation/useRollingStartNavigation";
import { useAppSelector } from "@src/store/hooks";
import { useAllProduct } from "@src/hooks/query/useAllProduct";
import { useAllCategory } from "@src/hooks/query/useAllCategory";
import useAllStore from "@src/hooks/query/useAllStore";
import { useConfiguration } from "@src/hooks/query/useConfiguration";
import useAllIngredient from "@src/hooks/query/useAllIngredient";
import runAfterLcp from "@src/utils/runAfterLcp";
import yieldMainThread from "@src/utils/yieldMainThread";
import { bootstrapBackboneCriticalConfiguration } from "./bootstrapBackboneCriticalConfiguration";
// Side-effect : déclare window.__RS_SETUP_PREFETCH__ (TTI path).
import "@src/lib/setupPrefetch";

/** TBT-FIX: clone profond pour SetupModel — `structuredClone` est nettement plus rapide que JSON sur de gros graphes. */
function cloneForSetupModel<T>(data: T): T {
  if (typeof structuredClone === "function") {
    try {
      return structuredClone(data);
    } catch {
      /* types non clonables (rare sur nos nœuds GraphQL) */
    }
  }
  return JSON.parse(JSON.stringify(data)) as T;
}

// @ts-ignore

export const mainCartInstance = new CartModel();
export const mainCustomerInstance = new CustomerModel();
// SETUP-REMOUNT-FIX: singleton module (comme mainCartInstance/mainCustomerInstance) —
// un useRef local à ModelProvider repart à null si le composant remonte, ce qui
// relance /setupweb + /setupprivate en double ; ce verrou survit au remount.
let setupModelInstance: SetupModelType | null = null;
export let rollingStartNavigation: IRollingStartNavigation | undefined;

export function defaultCart(cart?: CartModel): CartModel {
  return cart ? cart : app.getCart();
}

export type IBackboneContext = typeof backboneMap;

const backboneMap: {
  app: AppModel;
  // app$: Observable<AppModel | undefined>;
  cart: CartModel;
  setupPrivate?: SetupPrivateModelType;
  customer: CustomerModel;
  setup?: SetupModelType;
  models?: AppAttributes;
  loaded: boolean;
  updateChange: number;
  forceUpdate: () => void;
} = {
  app,
  cart: mainCartInstance,
  customer: mainCustomerInstance,
  // TBT-FIX: `setupPrivate` est assigné par `SetupModel` au moment de son
  // instantiation (post-LCP via `runAfterLcp`). À l'init du contexte, il
  // est `undefined` — comportement identique à l'ancien import statique
  // (la variable exportée par SetupModel est `let` déclarée et seulement
  // assignée dans `SetupModel.initialize`).
  setupPrivate: undefined,
  loaded: false,
  updateChange: 0,
  forceUpdate: () => +1,
};

export const ConnectBackboneToReactContext =
  createContext<IBackboneContext>(backboneMap);

export const BackboneProvider = ConnectBackboneToReactContext.Provider;

const ModelProvider = (props: {
  // runContext: RunContext;
  children: React.ReactNode;
}) => {
  const { children /* runContext */ } = props;
  // const appRef = useRef<AppModel>(app);
  // const { cart: cartState, customer: customerState } = useAppSelector(
  //   ({ cart, customer }) => ({
  //     cart,
  //     customer: customer.current,
  //   })
  // );
  // const [updateChanges, setUpdateChanges] = useState(backboneMap.updateChange);
  // const [object, setObject] = useState<AppAttributes | undefined>(undefined);
  const criticalConfigBootstrappedRef = useRef(false);
  const graphDataReadyRef = useRef(false);
  const setupInitialized = useAppSelector(
    (state) => state.app.setupInitialized
  );
  const backboneRevision = useAppSelector(
    (state) => state.app.backboneRevision
  );
  const pageTemplate = useAppSelector(
    (state) => state.page.currentPageContextIntl?.pageTemplate
  );
  const dispatch = useDispatch();
  const alertNotice = useAlertNoticeContext();

  // Prix listing : id_store localStorage → Redux avant setupweb (~+1–2s hasLocation).
  // HYDRATION-FIX: différer le dispatch après le 1er paint pour ne pas déclencher
  // React #421 sur les boundaries Suspense lazy du layout (cf. bootstrapBackboneCriticalConfiguration
  // plus bas — même pattern).
  useEffect(() => {
    const idStore = mainCartInstance.get("id_store");
    if (!isValidCartStoreId(idStore)) return;
    return runAfterLcp(
      () => {
        startTransition(() => {
          dispatch(
            cartSlice.actions.updateCurrentCart(mainCartInstance.attributes)
          );
        });
      },
      { timeout: 500, fallbackDelay: 0 }
    );
  }, [dispatch]);

  // Prix-PERF: relance cache /storecontext au mount (filet hors listing + // SetupModel).
  // Coalesce avec le prefetch vanilla ; markProducts via consumeWindowPrefetch (storeContext).
  // Attention: c'est un cache HTTP brut (window.__RS_STORECONTEXT_PREFETCH__),
  // il n'alimente PAS StoreModel._storeContextModel — donc pas hasStoreContext().
  useEffect(() => {
    void import("@src/lib/LocationFirst/storeContext").then(
      ({ ensureStoreContextFetch }) => {
        ensureStoreContextFetch();
      }
    );
  }, []);

  // STORECONTEXT-FIX: hasStoreContext() (modes de paiement, dispo produit,
  // stock) n'était rechargé qu'au 1er accès au listing (useInitSelectedStore).
  // Un F5 sur une autre page (home, produit…) avec un store déjà sélectionné
  // ne rechargeait jamais ce contexte. Filet générique — no-op si pas de
  // store en cart (donc coût nul en lab WebPerf, session sans localStorage) ;
  // différé post-LCP (comme les autres effets Backbone de ce fichier) pour ne
  // jamais concurrencer le paint initial / TTI sur le terrain.
  useEffect(() => {
    if (!setupInitialized) return;
    const idStore = mainCartInstance.get("id_store");
    if (!isValidCartStoreId(idStore)) return;
    const persistedStore = app.getStore(idStore);
    if (!persistedStore || persistedStore.hasStoreContext()) return;

    return runAfterLcp(
      () => {
        mainCartInstance.refreshStoreContext(persistedStore, () => {});
      },
      { timeout: 2500, fallbackDelay: 200 }
    );
  }, [setupInitialized]);

  const navigation = useRollingStartNavigation();
  // const isSSR = useIsSSR();

  // const forceRender = useCallback(() => {
  //   setUpdateChanges((c) => c + 1);
  // }, []);
  const { data: allProducts } = useAllProduct();
  const { data: allCategories } = useAllCategory();
  const { data: allStores } = useAllStore();
  const { data: allConfigurations } = useConfiguration();
  const allIngredient = useAllIngredient();

  // LCP-FIX: configs critiques Gatsby → `app.c()` sans attendre setupweb post-LCP.
  useEffect(() => {
    if (criticalConfigBootstrappedRef.current || !allConfigurations.length) {
      return;
    }
    criticalConfigBootstrappedRef.current = true;

    void bootstrapBackboneCriticalConfiguration(
      cloneForSetupModel(allConfigurations)
    ).then((collection) => {
      if (!collection) {
        criticalConfigBootstrappedRef.current = false;
        return;
      }
      // HYDRATION-FIX: différer le dispatch Redux après le 1er paint pour ne
      // pas déclencher React #421 sur les boundaries Suspense lazy du layout.
      runAfterLcp(
        () => {
          startTransition(() => {
            dispatch(AppSlice.actions.setCriticalConfigReady(true));
            dispatch(AppSlice.actions.notifyBackboneRevision());
          });
        },
        { timeout: 500, fallbackDelay: 0 }
      );
    });
  }, [allConfigurations, dispatch]);

  const graphDataReady =
    allProducts.length > 0 &&
    allCategories.length > 0 &&
    allStores.length > 0 &&
    allConfigurations.length > 0;

  useEffect(() => {
    graphDataReadyRef.current = graphDataReady;
  }, [graphDataReady]);

  useEffect(() => {
    if (setupInitialized || setupModelInstance || !graphDataReady) {
      return;
    }
    if (!pageTemplate) {
      return;
    }

    // LCP-FIX: `new SetupModel(...)` déclenche `/api/fr/setupweb?sync=initial`
    // (~270 KB) + instancie toutes les collections Backbone (CPU).
    // On diffère ça en post-LCP pour libérer la bande passante au moment du
    // paint initial. `setupInitialized` est positionné à true dans SetupModel
    // après `app.setupObjects()` (configurations Backbone disponibles pour app.c).
    // TBT-FIX: import dynamique → `SetupModel` (et son graphe SetupPrivate,
    // CategoryCollection lourd, etc.) sortent du chunk `app.js` eager.
    let aborted = false;

    const runSetupModel = () => {
      void (async () => {
        try {
          if (aborted || setupModelInstance || !graphDataReadyRef.current) {
            return;
          }
          // TTI-FIX: si le loader a déjà prefetch setupweb/setupprivate, on
          // n'étale plus 7 yields — le réseau a déjà fait le job, setupReady
          // peut arriver plus tôt. Sinon on garde le découpage TBT.
          const hasSetupPrefetch =
            typeof window !== "undefined" &&
            !!(
              window.__RS_SETUP_PREFETCH__?.web ||
              window.__RS_SETUP_PREFETCH__?.private
            );

          if (!hasSetupPrefetch) {
            await yieldMainThread();
            if (aborted) return;
          }

          const { default: SetupModelClass } = await import(
            /* webpackChunkName: "backbone-setup" */ "../model/SetupModel"
          );
          if (aborted || setupModelInstance) return;

          if (hasSetupPrefetch) {
            const product = cloneForSetupModel(allProducts);
            const category = cloneForSetupModel(allCategories);
            await yieldMainThread();
            if (aborted) return;
            const storeData = cloneForSetupModel(allStores);
            const configuration = cloneForSetupModel(allConfigurations);
            const ingredient = cloneForSetupModel(allIngredient);
            if (aborted || setupModelInstance) return;
            setupModelInstance = new SetupModelClass({
              product,
              category,
              store: storeData,
              configuration,
              ingredient,
            });
            return;
          }

          await yieldMainThread();
          const product = cloneForSetupModel(allProducts);
          await yieldMainThread();
          if (aborted) return;
          const category = cloneForSetupModel(allCategories);
          await yieldMainThread();
          if (aborted) return;
          const storeData = cloneForSetupModel(allStores);
          await yieldMainThread();
          if (aborted) return;
          const configuration = cloneForSetupModel(allConfigurations);
          await yieldMainThread();
          if (aborted) return;
          const ingredient = cloneForSetupModel(allIngredient);
          await yieldMainThread();
          if (aborted || setupModelInstance) return;
          setupModelInstance = new SetupModelClass({
            product,
            category,
            store: storeData,
            configuration,
            ingredient,
          });
        } catch (err) {
          console.error("[TBT] SetupModel dynamic import failed", err);
        }
      })();
    };

    // Scripts déjà gated LCP par le loader web-performance — pas de second
    // runAfterLcp ici (même règle sur toutes les pages).
    runSetupModel();

    return () => {
      aborted = true;
    };
  }, [graphDataReady, setupInitialized, pageTemplate]);

  // const sub$ = appObjects$.pipe(
  //   tap((o) => {
  //     setObject(o);
  //     // setAppInstance(app);
  //   })
  // );

  // const subscription = merge(sub$).subscribe();
  // return () => {
  // subscription.unsubscribe();
  // dispatch(AppSlice.actions.setSetupInitialized(false));
  // };
  // (bootstrap SetupModel : effet ci-dessus — deps stabilisées via `graphDataReady`)

  // useEffect(() => {
  // // const handler = () => {
  // //   startTransition(() => {
  // //     forceRender();
  // //   });
  // // };
  // const handlerCart = () => {
  //   console.log("======> handlerCart");
  //   const cartData = app.getCart()?.attributes;
  //   if (JSON.stringify(cartData) !== JSON.stringify(cartState.currentCart)) {
  //     startTransition(() => {
  //       dispatch(cartSlice.actions.updateCurrentCart(cartData));
  //       // forceRender();
  //     });
  //   }
  // };
  // // startTransition(() => {
  // handlerCart();
  // // });
  // // handler();
  // // app.on("change:*", handler);
  // app.getCart()?.on("change:*", handlerCart);
  // return () => {
  //   // app.off("change:*", handler);
  //   app.getCart()?.off("change:*", handlerCart);
  // };
  // }, []);

  useEffect(() => {
    if (!app) return;
    let teardown: (() => void) | undefined;
    let aborted = false;
    // TBT-FIX: enregistrer les handlers modales/alertes après 1er paint + idle,
    // pour ne pas empiler sur le même burst que l'hydratation React.
    // Import dynamique pour sortir initAppEvents + nanoid du chunk app.js eager.
    const cancelSchedule = runAfterLcp(
      () => {
        void import(
          /* webpackChunkName: "backbone-init-events" */ "./initAppEvents"
        ).then(({ initAppEvents }) => {
          if (aborted) return;
          teardown = initAppEvents(app, alertNotice);
        });
      },
      { timeout: 2500, fallbackDelay: 200 }
    );
    return () => {
      aborted = true;
      cancelSchedule();
      teardown?.();
    };
  }, [alertNotice]);

  useEffect(() => {
    rollingStartNavigation = navigation;
  }, [navigation]);

  // listen on CustomerModel change
  // useEffect(() => {
  //   const handleCustomerChange = (...args: any[]) => {
  //     // forceRender();
  //     if (
  //       JSON.stringify(customerState) !==
  //       JSON.stringify(app.getCustomer().attributes)
  //     ) {
  //       // console.log("======> handleCustomerChange");
  //       startTransition(() => {
  //         dispatch(
  //           customerSlice.actions.setCurrentCustomer(
  //             app.getCustomer().attributes
  //           )
  //         );
  //       });
  //     }
  //   };
  //   // first init
  //   // handleCustomerChange();

  //   // listner
  //   app.getCustomer().on("all", handleCustomerChange);

  //   return () => {
  //     app.getCustomer().off("all", handleCustomerChange);
  //   };
  // }, []);

  // const value = useMemo(
  //   () => {
  //     return {
  //       ...backboneMap,
  //       // forceUpdate: forceRender,
  //       app: appRef.current || app,
  //       // updateChange: updateChanges,
  //     };
  //   },
  //   [
  //     /* forceRender, updateChanges */
  //   ]
  // );

  const backboneContextValue = useMemo(
    () => ({
      ...backboneMap,
      updateChange: backboneRevision,
      loaded: setupInitialized,
    }),
    [backboneRevision, setupInitialized]
  );

  return <BackboneProvider value={backboneContextValue} children={children} />;
};

export default React.memo(ModelProvider);
